import type { ReactNode } from "react";

import { cx } from "@/utils/cx";

interface MapEmptyStateProps {
	action?: ReactNode;
	className?: string;
	description?: ReactNode;
	fullHeight?: boolean;
	icon?: ReactNode;
	/** When true, wraps the icon in a spinning loader ring. */
	loading?: boolean;
	title: string;
}

/** Compact empty-state used by Map Configuration (PR StatusMessage subset). */
export const MapEmptyState = ({
	action,
	className,
	description,
	fullHeight,
	icon,
	loading = false,
	title,
}: MapEmptyStateProps) => (
	<div
		className={cx(
			"relative flex h-full w-full flex-col items-center justify-center gap-3 overflow-hidden rounded-lg border border-secondary_alt px-6 py-12 text-center",
			fullHeight && "min-h-[32rem] flex-1",
			className,
		)}
	>
		{icon ? (
			<div
				aria-label={loading ? "Loading" : undefined}
				className={cx(
					"relative flex items-center justify-center",
					loading ? "size-16" : "size-12",
				)}
				role={loading ? "status" : undefined}
			>
				{loading ? (
					<svg
						aria-hidden
						className="absolute inset-0 size-full animate-spin"
						fill="none"
						viewBox="0 0 64 64"
					>
						<circle
							className="text-bg-tertiary"
							cx="32"
							cy="32"
							r="28"
							stroke="currentColor"
							strokeWidth="4"
						/>
						<circle
							className="stroke-fg-brand-primary"
							cx="32"
							cy="32"
							fill="none"
							r="28"
							strokeDasharray="120"
							strokeDashoffset="90"
							strokeLinecap="round"
							strokeWidth="4"
						/>
					</svg>
				) : null}
				<div
					className={cx(
						"flex size-12 items-center justify-center rounded-full bg-primary text-fg-brand-primary [&_svg]:size-6",
						!loading && "ring-brand-primary ring-4",
					)}
				>
					{icon}
				</div>
			</div>
		) : null}
		<div className="flex max-w-sm flex-col gap-1">
			<p className="text-md font-semibold text-primary">{title}</p>
			{description ? (
				<p className="text-sm text-tertiary">{description}</p>
			) : null}
		</div>
		{action ? <div className="mt-1">{action}</div> : null}
	</div>
);
